/**
 * This file exposes frequently used parsers methods.
 *
 * These methods are auto-imported in components and pages and can be used
 * directly in templates, <script setup> or anywhere in the Options API without
 * having to manually import them.
 */
import type { CountryCode } from 'libphonenumber-js'
import type { Nullable } from '~/types'

/**
 * Parses a phone number.
 *
 * @param phoneNumber The phone number to parse.
 * @param countryPhoneCode The country phone code.
 * @param phoneNumberIncludesCountryPhoneCode Whether the phone number includes the country phone code.
 * @param countryCode The country code.
 * @returns The parsed phone number.
 *
 * @example parsePhoneNumber({ phoneNumber: '1234567890' })
 * @example parsePhoneNumber({ phoneNumber: '1234567890', countryPhoneCode: '1', phoneNumberIncludesCountryPhoneCode: true, countryCode: 'US' })
 */
export async function parsePhoneNumber({
  countryPhoneCode = '+1',
  phoneNumber,
  phoneNumberIncludesCountryPhoneCode = false,
  countryCode,
}: {
  countryPhoneCode?: string
  phoneNumber?: string
  phoneNumberIncludesCountryPhoneCode?: boolean
  countryCode?: CountryCode
}) {
  if (!phoneNumber) {
    return
  }

  const { parsePhoneNumberFromString } = await import('libphonenumber-js')

  if (phoneNumberIncludesCountryPhoneCode) {
    return parsePhoneNumberFromString(phoneNumber, countryCode)
  }

  // Add plus sign if CountryPhoneCode doesn't start with one.
  return parsePhoneNumberFromString(
    `${countryPhoneCode?.indexOf('+') === -1 ? '+' : ''}${countryPhoneCode}${phoneNumber}`,
    countryCode,
  )
}

/**
 * Parses tracking parameters from a string.
 *
 * @param utms The UTM parameters to parse.
 * @param rtsessionid The RTSession ID to parse.
 * @param isDeepLinkRequest Whether the request is a deep link request.
 * @param addRtsessionid Whether to add the RTSession ID to the UTM parameters.
 * @returns The parsed tracking parameters.
 *
 * @example parseTrackingParameters({ utms: new URLSearchParams('utm_source=test&utm_medium=test&utm_campaign=test'), rtsessionid: '123', isDeepLinkRequest: false, addRtsessionid: true })
 */
export function parseTrackingParameters({
  utms,
  rtsessionid,
  isDeepLinkRequest = false,
  addRtsessionid = true,
}: {
  utms: URLSearchParams
  rtsessionid?: Nullable<string>
  isDeepLinkRequest?: boolean
  addRtsessionid?: boolean
}) {
  if (isDeepLinkRequest && utms) {
    if (addRtsessionid && rtsessionid) {
      utms.set('rtsessionid', rtsessionid)
    }

    // Remove empty values from the UTM parameters.
    for (const [key, value] of utms.entries()) {
      if (value === '') {
        utms.delete(key)
      }
    }
  }

  return { utms: utms.toString(), rtsessionid }
}
